import FileSystem from '@/models/FileSystem'
import Record from '@/models/Record'
import * as JSZip from 'jszip'

export default {
  /**
   * Prompts user to download specified url under specified filename.
   *
   * @param {string} filename filename to be displayed to user
   * @param {string|Blob} url actual url
   */
  download (filename, url) {
    let isBlob = url instanceof Blob

    if (!isBlob || this.isObjectURLSupported()) {
      var link = document.createElement('a')
      document.body.appendChild(link)
      link.href = isBlob ? window.URL.createObjectURL(url) : url
      link.download = filename
      link.click()
      link.remove()
    } else {
      window.navigator.msSaveBlob(url, filename)
    }
  },

  /**
   * @returns {boolean} if downloading blob using object url is supported
   */
  isObjectURLSupported () {
    return !window.navigator.msSaveOrOpenBlob
  },

  /**
   * Collects recording data and metadata and returns them.
   * @param {Object} recording recording database object (only id is used)
   * @param {(download: number, metadata: number) => void} progressCallback progress callback
   * @returns {Promise<{ recording: ArrayBuffer, metadata: string }>} promise loading data
   */
  collectRecording (recording, progressCallback) {
    // Result item with recording data and metadata text
    let result = {
      recording: null,
      metdata: null
    }

    // Recording can already have data downloaded
    let promise
    if (recording.blob == null) {
      promise = FileSystem
        .getRecording(
          recording.id,
          'download',
          (loaded, total) => {
            progressCallback(loaded / total, 0)
          }
        )
    } else {
      promise = Promise.resolve(recording.blob)
    }

    return promise
      .then(response => {
        progressCallback && progressCallback(1, 0)

        result.recording = response

        // Fetch metadata
        return Record.getMetadata(recording)
      }, err => {
        console.error('[Downloader]', 'Failed to fetch recording contents')
        console.error(err)
        // Failed to fetch recording
        // Try to at least fetch metadata
        return Record.getMetadata(recording)
      })
      .then(data => {
        progressCallback && progressCallback(1, 1)

        result.metadata = data
        return result
      }, err => {
        console.error('[Downloader]', 'Failed to fetch recording metdata')
        console.error(err)
      })
  },

  /**
   * Downloads specified list of recordings.
   *
   * @param {Object[]} items list of db objects to download
   * @param {string} filename name of resulting archive
   * @param {(done: number, total: number) => void} progressCallback progress callback
   * @returns {Promise<void>}
   */
  downloadRecordings (items, filename, progressCallback) {
    let zip = new JSZip()
    let downloaded = []
    let playable = items
      .filter(item => Record.isPlayable(item))

    return Promise.all(
      playable
        .map(item => {
          let downloadIndex = downloaded.length
          downloaded.push(0)

          // Download recording into zip
          return this
            .collectRecording(
              item,
              (download, metadata) => {
                downloaded[downloadIndex] = download * 0.95 + metadata * 0.05
                progressCallback && progressCallback(downloaded.reduce((s, i) => s + i, 0), playable.length)
              }
            )
            .then(result => {
              zip.file(Record.getFilename(item), result.recording || '')
              zip.file(Record.getBasename(item) + '.txt', result.metadata || '')
            })
            .catch(err => {
              console.error('[Download]', 'Failed to collect recording data')
              console.error(err)
            })
        })
    ).then(() => {
      // Generate zip archive
      return zip.generateAsync({ type: 'uint8array' })
    }).then(result => {
      // Download zip archive
      let blob = new Blob([result], { type: 'application/zip' })
      this.download(filename || 'recordings.zip', blob)
    })
  }
}



// WEBPACK FOOTER //
// ./src/utils/Downloader.js